import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users, ListChecks, Lightbulb, Sparkles, Mail, ArrowLeft, ShieldAlert,
  MessageSquare, Download, RotateCcw,
} from "lucide-react";
import { client, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";
const SEAL = "var(--seal)";

const PLAN_OPTIONS = [
  { id: "starter", label: "Starter (5/mo)" },
  { id: "creator", label: "Creator (40/mo)" },
  { id: "studio", label: "Studio (200/mo)" },
  { id: "lifetime", label: "Lifetime (100/mo)" },
  { id: "owner", label: "Owner (unlimited)" },
];

const PLAN_COLOR = {
  starter: INK_55,
  creator: SEAL,
  studio: SEAL,
  lifetime: "#d8a857",
  owner: "#e0b76a",
};

function UserRow({ u, onChanged }) {
  const [busy, setBusy] = useState(false);
  const isOwnerEmail = (u.email || "").toLowerCase() === "pradeepmunjal029@gmail.com";

  const changePlan = async (newPlan) => {
    if (newPlan === u.plan) return;
    setBusy(true);
    try {
      const r = await client.patch(`/admin/users/${u.user_id}/plan`, { plan: newPlan });
      toast.success(`${u.email} → ${r.data.plan}`);
      onChanged({ user_id: u.user_id, plan: r.data.plan });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update plan");
    } finally {
      setBusy(false);
    }
  };

  const resetQuota = async () => {
    if (!window.confirm(`Reset this month's AI quota for ${u.email}?`)) return;
    setBusy(true);
    try {
      await client.post(`/admin/users/${u.user_id}/reset-quota`);
      toast.success(`Quota reset for ${u.email}`);
      onChanged({ user_id: u.user_id, ai_used_this_month: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="py-3 flex flex-col md:flex-row md:items-center gap-3 md:gap-4"
      style={{ borderBottom: "1px solid var(--ink-22)" }}
      data-testid={`admin-user-${u.user_id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="text-sm truncate" style={{ color: "var(--ink)" }}>
          {u.name || u.email}
          {isOwnerEmail && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded" style={{ color: PLAN_COLOR.owner, border: `1px solid ${PLAN_COLOR.owner}` }}>
              Owner
            </span>
          )}
        </div>
        <div className="text-[10px] mt-0.5 truncate" style={{ color: INK_55 }}>{u.email}</div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <div className="text-[10px] font-mono" style={{ color: INK_55 }} title="AI scripts used this month">
          {u.ai_used_this_month ?? 0}
          {u.plan === "owner" ? "" : ` / ${u.ai_limit ?? 5}`}
        </div>
        <select
          value={u.plan || "starter"}
          onChange={(e) => changePlan(e.target.value)}
          disabled={busy || isOwnerEmail}
          className="text-xs rounded px-2 py-1.5"
          style={{
            background: "var(--bg)",
            color: PLAN_COLOR[u.plan] || INK_55,
            border: `1px solid ${PLAN_COLOR[u.plan] || "var(--ink-22)"}`,
            opacity: isOwnerEmail ? 0.7 : 1,
          }}
          data-testid={`admin-user-plan-${u.user_id}`}
          title={isOwnerEmail ? "Owner plan is locked to this email" : "Change plan"}
        >
          {PLAN_OPTIONS.map((p) => (
            <option key={p.id} value={p.id}>{p.label}</option>
          ))}
        </select>
        <button
          onClick={resetQuota}
          disabled={busy}
          className="btn-ghost text-[11px] flex-shrink-0"
          title="Reset this month's AI quota"
          data-testid={`admin-user-reset-${u.user_id}`}
        >
          <RotateCcw size={11} />
        </button>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, testid }) {
  return (
    <div className="card" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <div className="eyebrow">{label}</div>
        <Icon size={14} style={{ color: SEAL }} />
      </div>
      <div className="font-heading text-4xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
        {value}
      </div>
      {sub && <div className="text-xs mt-1" style={{ color: INK_55 }}>{sub}</div>}
    </div>
  );
}

async function downloadExport(collection, fmt) {
  const token = localStorage.getItem("session_token");
  const res = await fetch(`${API}/admin/export/${collection}?fmt=${fmt}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `Export failed (${res.status})`);
  }
  const blob = await res.blob();
  const cd = res.headers.get("content-disposition") || "";
  const match = cd.match(/filename="?([^";]+)"?/);
  const filename = match ? match[1] : `${collection}.${fmt}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function ExportButtons({ collection }) {
  const [busy, setBusy] = useState("");
  const handle = async (fmt) => {
    setBusy(fmt);
    try {
      await downloadExport(collection, fmt);
    } catch (e) {
      alert(e.message || "Export failed");
    } finally {
      setBusy("");
    }
  };
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => handle("csv")}
        disabled={!!busy}
        className="btn-secondary text-[10px] uppercase tracking-[0.18em] px-3 py-1.5"
        data-testid={`admin-export-${collection}-csv`}
      >
        <Download size={12} />
        {busy === "csv" ? "…" : "CSV"}
      </button>
      <button
        type="button"
        onClick={() => handle("json")}
        disabled={!!busy}
        className="btn-secondary text-[10px] uppercase tracking-[0.18em] px-3 py-1.5"
        data-testid={`admin-export-${collection}-json`}
      >
        <Download size={12} />
        {busy === "json" ? "…" : "JSON"}
      </button>
    </div>
  );
}

const KIND_COLOR = {
  bug: "#c97a6a",
  idea: "var(--seal)",
  love: "#d4a373",
  other: "var(--ink-55)",
};

export default function Admin() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [waitlist, setWaitlist] = useState([]);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const [s, w, u, f] = await Promise.all([
          client.get("/admin/stats"),
          client.get("/admin/waitlist"),
          client.get("/admin/users"),
          client.get("/admin/feedback"),
        ]);
        setStats(s.data);
        setWaitlist(w.data);
        setUsers(u.data);
        setFeedback(f.data);
      } catch (e) {
        setError(e?.response?.data?.detail || "Could not load admin data.");
      }
    })();
  }, []);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <ShieldAlert size={32} style={{ color: "#c97a6a", margin: "0 auto" }} />
        <h1 className="font-heading text-3xl mt-6" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
          {error}
        </h1>
        <p className="text-sm mt-3" style={{ color: INK_55 }}>
          Signed in as <span style={{ color: "var(--ink)" }}>{user?.email}</span>
        </p>
        <button onClick={() => navigate("/dashboard")} className="btn-secondary mt-6" data-testid="admin-back">
          <ArrowLeft size={14} /> Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> ix. Admin</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            The <span className="font-display-italic">founder</span> view.
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatCard icon={Users} label="i. Users" value={stats?.users_total ?? "—"} sub={`+${stats?.users_7d ?? 0} last 7d · +${stats?.users_30d ?? 0} last 30d`} testid="admin-stat-users" />
        <StatCard icon={Mail} label="ii. Waitlist" value={stats?.waitlist_total ?? "—"} sub={`+${stats?.waitlist_7d ?? 0} last 7d`} testid="admin-stat-waitlist" />
        <StatCard icon={Lightbulb} label="iii. Ideas" value={stats?.ideas_total ?? "—"} sub="across all users" testid="admin-stat-ideas" />
        <StatCard icon={Sparkles} label="iv. AI scripts" value={stats?.scripts_total ?? "—"} sub="GPT-5.2 generations" testid="admin-stat-scripts" />
      </div>

      {/* Quick export bar */}
      <div className="card mt-4" data-testid="admin-export-bar">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="eyebrow">v. Export data</div>
            <div className="text-xs mt-1" style={{ color: INK_55 }}>
              Snapshot any collection as CSV (Excel-friendly) or JSON (raw backup).
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          {[
            { id: "users", label: "Users" },
            { id: "waitlist", label: "Waitlist" },
            { id: "feedback", label: "Feedback" },
            { id: "ideas", label: "Ideas" },
          ].map((c) => (
            <div key={c.id} className="flex items-center justify-between gap-2 px-3 py-2 rounded" style={{ border: "1px solid var(--ink-22)" }}>
              <span className="text-sm" style={{ color: "var(--ink)" }}>{c.label}</span>
              <ExportButtons collection={c.id} />
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">Waitlist</div>
            <ListChecks size={14} style={{ color: SEAL }} />
          </div>
          {waitlist.length === 0 ? (
            <div className="text-sm py-6 text-center" style={{ color: INK_55 }}>No waitlist signups yet.</div>
          ) : (
            <div className="space-y-1" style={{ borderTop: "1px solid var(--ink-22)" }} data-testid="admin-waitlist-list">
              {waitlist.map((w) => (
                <div key={w.id} className="py-3 flex items-center justify-between gap-3" style={{ borderBottom: "1px solid var(--ink-22)" }}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--ink)" }}>{w.email}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] mt-1" style={{ color: INK_55 }}>
                      {w.source || "landing"}
                    </div>
                  </div>
                  <div className="text-[10px] font-mono" style={{ color: INK_55 }}>
                    {(w.created_at || "").slice(0, 10)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="card">
          <div className="flex items-center justify-between mb-5">
            <div className="eyebrow">Users · plans</div>
            <Users size={14} style={{ color: SEAL }} />
          </div>
          {users.length === 0 ? (
            <div className="text-sm py-6 text-center" style={{ color: INK_55 }}>No users yet.</div>
          ) : (
            <div className="space-y-1" style={{ borderTop: "1px solid var(--ink-22)" }} data-testid="admin-users-list">
              {users.map((u) => (
                <UserRow key={u.user_id} u={u} onChanged={(updated) => {
                  setUsers((prev) => prev.map((x) => x.user_id === updated.user_id ? { ...x, ...updated } : x));
                }} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Feedback section — full width */}
      <div className="card mt-4" data-testid="admin-feedback-card">
        <div className="flex items-center justify-between mb-5">
          <div className="eyebrow">vi. Feedback</div>
          <MessageSquare size={14} style={{ color: SEAL }} />
        </div>
        {feedback.length === 0 ? (
          <div className="text-sm py-6 text-center" style={{ color: INK_55 }}>
            No feedback yet. When users submit via the floating button, it lands here.
          </div>
        ) : (
          <div className="space-y-1" style={{ borderTop: "1px solid var(--ink-22)" }} data-testid="admin-feedback-list">
            {feedback.map((f) => (
              <div key={f.id} className="py-4 flex flex-col sm:flex-row sm:items-start gap-3" style={{ borderBottom: "1px solid var(--ink-22)" }}>
                <div className="flex items-center gap-2 sm:w-40 flex-shrink-0">
                  <span
                    className="text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded"
                    style={{
                      color: KIND_COLOR[f.kind] || INK_55,
                      border: `1px solid ${KIND_COLOR[f.kind] || "var(--ink-22)"}`,
                    }}
                  >
                    {f.kind || "other"}
                  </span>
                  <span className="text-[10px] font-mono" style={{ color: INK_55 }}>
                    {(f.created_at || "").slice(0, 10)}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm whitespace-pre-wrap" style={{ color: "var(--ink)" }}>
                    {f.message}
                  </div>
                  <div className="text-[10px] mt-2 truncate" style={{ color: INK_55 }}>
                    {f.email || "anonymous"} · {f.page || "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-xs mt-8 text-center" style={{ color: INK_55 }}>
        Admin access is limited to whitelisted founder emails.
      </div>
    </div>
  );
}
